// Handles the redirect from the Cognito Hosted UI. Exchanges the auth code
// for an access token + refresh cookie, then navigates the SPA back to the
// post-login path that was stashed before the redirect.

import { useEffect, useState } from "react";
import { completeLogin } from "../lib/auth.js";
import { useAuth } from "../store/auth.js";

export function AuthCallback(): JSX.Element {
  const { setSession } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await completeLogin();
        if (cancelled) return;
        setSession({
          accessToken: result.accessToken,
          expiresAtMs: result.expiresAtMs,
          idToken: result.idToken,
        });
        // Strip query params and navigate to the requested page.
        window.history.replaceState({}, "", result.postLoginPath);
        // Force a re-render at the new path. With no router, the simplest
        // correct behaviour is a hard reload.
        window.location.assign(result.postLoginPath);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setSession]);

  return (
    <main className="min-h-dvh flex items-center justify-center p-6">
      <div className="max-w-sm w-full text-center space-y-3">
        {error ? (
          <>
            <h1 className="text-xl font-semibold">Sign-in failed</h1>
            <p className="text-red-400 text-sm break-all">{error}</p>
            <a className="text-sky-400 underline text-sm" href="/">
              return home
            </a>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold">Signing you in…</h1>
            <p className="text-slate-400 text-sm">One moment.</p>
          </>
        )}
      </div>
    </main>
  );
}
